import { NavLink, Outlet, useNavigate } from "react-router-dom";
import ScrollMemory from "@/components/ScrollMemory";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Truck, FileText, Users, Box, Wallet,
  ArrowDownToLine, ArrowUpFromLine, LogOut, Package,
  Settings as SettingsIcon, Sheet, BookOpen, ClipboardList,
  Upload, FileJson, History, ScrollText, UserCog,
} from "lucide-react";

const sections = [
  { title: null, items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  ]},
  { title: "Operations", items: [
    { to: "/trucks", label: "Truck Entries", icon: Truck, testId: "nav-trucks" },
    { to: "/bills", label: "Bills", icon: FileText, testId: "nav-bills" },
    { to: "/payments-received", label: "Payments Received", icon: ArrowDownToLine, testId: "nav-payments-received" },
    { to: "/payments-made", label: "Payments Made", icon: ArrowUpFromLine, testId: "nav-payments-made" },
    { to: "/bulk-import", label: "Bulk Import", icon: Upload, testId: "nav-bulk-import" },
  ]},
  { title: "Ledgers", items: [
    { to: "/party-ledgers", label: "Party Ledgers", icon: BookOpen, testId: "nav-party-ledgers" },
    { to: "/transporter-ledgers", label: "Transporter Ledgers", icon: BookOpen, testId: "nav-transporter-ledgers" },
    { to: "/accounts-sheet", label: "Accounts Sheet", icon: Sheet, testId: "nav-accounts-sheet" },
    { to: "/bill-register", label: "Bill Register", icon: ScrollText, testId: "nav-bill-register" },
    { to: "/gstr1", label: "GSTR-1 Report", icon: FileJson, testId: "nav-gstr1" },
  ]},
  { title: "Masters", items: [
    { to: "/parties", label: "Parties", icon: Users, testId: "nav-parties" },
    { to: "/transporters", label: "Transporters", icon: Package, testId: "nav-transporters" },
    { to: "/materials", label: "Materials", icon: Box, testId: "nav-materials" },
    { to: "/pos", label: "Purchase Orders", icon: ClipboardList, testId: "nav-pos" },
  ]},
  { title: null, items: [
    { to: "/audit-log", label: "Audit Log", icon: History, testId: "nav-audit-log", adminOnly: true },
    { to: "/users", label: "Users", icon: UserCog, testId: "nav-users", adminOnly: true },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
  ]},
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [company, setCompany] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => setCompany(r.data || {})).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-background bg-grain">
      <aside className="no-print w-64 shrink-0 bg-brand-forest text-white flex flex-col">
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          {company.logo_base64 ? (
            <img
              src={company.logo_base64}
              alt="Logo"
              data-testid="sidebar-logo"
              className="h-12 w-12 rounded-full object-cover bg-white/95 ring-1 ring-white/20 shadow-sm shrink-0"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-brand-sand/20 ring-1 ring-white/15 shrink-0"></div>
          )}
          <div className="leading-tight min-w-0">
            <div className="font-serif text-lg tracking-tight truncate">TULSI <span className="text-brand-sand">TRADING</span></div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/60 mt-0.5">& Logistics</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {sections.map((sec, si) => {
            const items = sec.items.filter((it) => !it.adminOnly || user?.role === "admin");
            if (items.length === 0) return null;
            return (
            <div key={si}>
              {sec.title && <div className="px-3 mb-1.5 text-[9px] uppercase tracking-[0.25em] text-white/40">{sec.title}</div>}
              <div className="space-y-0.5">
                {items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                        isActive ? "bg-brand-sand/15 text-brand-sand" : "text-white/75 hover:bg-white/5 hover:text-white"
                      }`}>
                    <it.icon className="h-4 w-4 stroke-[1.5]" />{it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );})}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="text-xs text-white/60">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          {user?.role && (
            <div className={`mt-1 inline-block px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.2em] ${
              user.role === "admin" ? "bg-brand-sand/20 text-brand-sand" : "bg-white/10 text-white/70"
            }`} data-testid="user-role-badge">{user.role}</div>
          )}
          <button data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] py-2 border border-white/20 rounded-sm hover:bg-white/5 transition-colors">
            <LogOut className="h-3.5 w-3.5" /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-auto">
        <ScrollMemory />
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8 border-b border-brand-line pb-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-muted mb-2">Tulsi Trading & Logistics</div>
        <h1 className="font-serif text-4xl text-brand-ink tracking-tight">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-brand-muted max-w-2xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
